/**
 * Realtor Plugin MQTT Provider
 * 
 * Глобальный обработчик MQTT событий для плагина Realtor.
 * Подписывается на все события плагина и обрабатывает их централизованно.
 */

import { useQueryClient } from '@tanstack/react-query';
import { usePluginEvents } from '../../mqtt/hooks';
import { EVENT_TYPES, FeedSyncCompletedPayload, FeedSyncFailedPayload } from '../../types/event';
import { notifications } from '@mantine/notifications';

export function RealtorMQTTProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  // Developer events
  usePluginEvents('realtor', EVENT_TYPES.DEVELOPER_DELETED, () => {
    console.log('[RealtorMQTT] Developer deleted');
    queryClient.invalidateQueries({ queryKey: ['realtor', 'developers'] });
    queryClient.invalidateQueries({ queryKey: ['realtor', 'residential-complexes'] });
    queryClient.invalidateQueries({ queryKey: ['residential-complexes'] });
    queryClient.invalidateQueries({ queryKey: ['realtor', 'apartments'] });
    queryClient.invalidateQueries({ queryKey: ['realtor', 'feeds'] });
    notifications.show({ title: 'Удалено', message: 'Застройщик удален', color: 'blue' });
  }, [queryClient]);

  // Residential complex events
  usePluginEvents('realtor', EVENT_TYPES.RESIDENTIAL_COMPLEX_DELETED, () => {
    console.log('[RealtorMQTT] Residential complex deleted');
    queryClient.invalidateQueries({ queryKey: ['realtor', 'residential-complexes'] });
    queryClient.invalidateQueries({ queryKey: ['residential-complexes'] });
    queryClient.invalidateQueries({ queryKey: ['realtor', 'apartments'] });
    notifications.show({ title: 'Удалено', message: 'ЖК удален', color: 'blue' });
  }, [queryClient]);

  // Apartment events
  usePluginEvents('realtor', EVENT_TYPES.APARTMENT_DELETED, () => {
    console.log('[RealtorMQTT] Apartment deleted');
    queryClient.invalidateQueries({ queryKey: ['realtor', 'apartments'] });
    notifications.show({ title: 'Удалено', message: 'Квартира удалена', color: 'blue' });
  }, [queryClient]);

  // Feed events
  usePluginEvents('realtor', EVENT_TYPES.FEED_DELETED, () => {
    console.log('[RealtorMQTT] Feed deleted');
    queryClient.invalidateQueries({ queryKey: ['realtor', 'feeds'] });
    notifications.show({ title: 'Удалено', message: 'Фид удален', color: 'blue' });
  }, [queryClient]);

  usePluginEvents('realtor', EVENT_TYPES.FEED_SYNC_STARTED, (event) => {
    const payload = event.payload as { feed_id: string };
    console.log('[RealtorMQTT] Feed sync started:', payload.feed_id);
  }, []);

  usePluginEvents('realtor', EVENT_TYPES.FEED_SYNC_COMPLETED, (event) => {
    const payload = event.payload as FeedSyncCompletedPayload;
    console.log('[RealtorMQTT] Feed sync completed:', payload.feed_id);
    queryClient.invalidateQueries({ queryKey: ['realtor', 'feeds'] });
    queryClient.invalidateQueries({ queryKey: ['realtor', 'apartments'] });
    notifications.show({
      title: 'Синхронизация завершена',
      message: `Объектов: ${payload.total_offers} (новых: ${payload.new_apartments}, обновлено: ${payload.updated})`,
      color: 'green',
    });
  }, [queryClient]);

  usePluginEvents('realtor', EVENT_TYPES.FEED_SYNC_FAILED, (event) => {
    const payload = event.payload as FeedSyncFailedPayload;
    console.log('[RealtorMQTT] Feed sync failed:', payload.error);
    queryClient.invalidateQueries({ queryKey: ['realtor', 'feeds'] });
    notifications.show({
      title: 'Ошибка синхронизации',
      message: payload.error,
      color: 'red',
    });
  }, [queryClient]);

  return <>{children}</>;
}
